import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { useNavigate, useSearchParams } from "react-router-dom"
import { getTintVTOService } from "services/tintvto"
import { IntegrationType, type UserData } from "types"
import { useAlert } from "providers/AlertProvider"
import { FixedHeaderLayout } from "components/FixedHeaderLayout/FixedHeaderLayout"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { PasswordInput } from "@/components/ui/password-input"
import { Label } from "@/components/ui/label"
import { getGenericUserErrorMessage } from "@/utils/userFacingErrors"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"

const groups = ["Admin", "Merchant", "Editor"]

interface UserPageProps {
  add?: boolean
}

export const UserPage = ({ add }: UserPageProps) => {
  const { showAlert } = useAlert()
  const { t } = useTranslation()
  const service = getTintVTOService()
  const [searchParams] = useSearchParams()
  const username = searchParams.get("username")
  const group = searchParams.get("group")
  const navigate = useNavigate()

  const [loading, setLoading] = useState(false)
  const [userData, setUserData] = useState<UserData>({
    username: "",
    password: "",
    group: null,
    merchantId: "",
    integrationType: IntegrationType.COMMON,
  })

  useEffect(() => {
    if (add)
      setUserData({
        password: "",
        username: "",
        group: null,
        merchantId: "",
        integrationType: IntegrationType.COMMON,
      })
    else if (username && group)
      setUserData({
        password: "",
        username,
        group,
        merchantId: "",
        integrationType: IntegrationType.COMMON,
      })
  }, [add, username, group])

  const update = (patch: Partial<UserData>) => {
    setUserData((prev) => ({ ...prev, ...patch }))
  }

  const isValid = () => {
    return (
      userData.username?.length > 1 &&
      userData.password?.length > 1 &&
      (!userData.group || userData.group.length > 1)
    )
  }

  const handleSave = async () => {
    setLoading(true)
    let error: { message: string } | false | undefined
    try {
      if (add) {
        const response = await service.registerUser(userData)
        error = response.errors ? response.errors[0] : false
        showAlert({
          error: !!error,
          text: error
            ? getGenericUserErrorMessage(t)
            : t("userPage.created", { username: userData.username }),
        })
      } else {
        const response = await service.updateUser(userData)
        error = response.errors ? response.errors[0] : false
        showAlert({
          error: !!error,
          text: error
            ? getGenericUserErrorMessage(t)
            : t("userPage.updated", { username: userData.username }),
        })
      }
    } catch (err) {
      error = JSON.parse(JSON.stringify(err))?.graphQLErrors[0]
      showAlert({
        error: !!error,
        text: error
          ? getGenericUserErrorMessage(t)
          : t("userPage.updated", { username: userData.username }),
      })
    }
    setLoading(false)
    if (!error) navigate("/users")
  }

  return (
    <div data-testid={add ? "user-add-page" : "user-edit-page"}>
      <FixedHeaderLayout
        title={add ? t("userPage.addUser") : t("userPage.editUser")}
        onBack={() => navigate("/users")}
        backLabel={t("backToUsers")}
        onFirstButtonClicked={() => navigate("/users")}
        onSecondButtonClicked={handleSave}
        onSecondButtonLoading={loading}
        validateSecondButton={isValid}
      />

      <div className="p-6">
        <div className="max-w-2xl">
          <Card data-testid="user-form-card">
            <CardHeader>
              <CardTitle className="text-base">Account details</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4">
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="username">
                    {t("username")} <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="username"
                    data-testid="user-username-input"
                    disabled={!add}
                    value={userData.username}
                    onChange={(e) => update({ username: e.target.value })}
                  />
                </div>

                <div className="flex flex-col gap-1.5">
                  <Label>
                    {t("group")} <span className="text-destructive">*</span>
                  </Label>
                  <Select
                    disabled={!add}
                    value={userData.group ?? undefined}
                    onValueChange={(value) => update({ group: value })}
                  >
                    <SelectTrigger data-testid="user-group-select">
                      <SelectValue placeholder={t("selectGroup")} />
                    </SelectTrigger>
                    <SelectContent data-testid="user-group-options">
                      {groups.map((g) => (
                        <SelectItem value={g} key={g} data-testid="user-group-option">
                          {g}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {userData.integrationType === IntegrationType.COMMON && (
                  <div className="col-span-2 flex flex-col gap-1.5">
                    <Label htmlFor="new-password">
                      {add ? t("password") : t("newPassword")}{" "}
                      <span className="text-destructive">*</span>
                    </Label>
                    <PasswordInput
                      id="new-password"
                      data-testid="user-password-input"
                      autoComplete="new-password"
                      value={userData.password}
                      onChange={(e) => update({ password: e.target.value })}
                    />
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  )
}
